import React, { useCallback, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@ge/components/loader';
import { useColumnState } from '@ge/components/table/use-column-state';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';

import { TenantContext } from '../context/tenant-context';
import { useRolesTable } from '../data-hooks/use-roles-table';
import { tenantRolesCols, RolesColumnDefs } from '../models/roles-table-cols';

import BackToGlobalTablesButton from './back-to-global-tables-button';
import RolesTable from './tables/roles-table';
import TenantAdminActionsHeader from './tenant-admin-actions-header';

const SORT_STATE_ID = 'admin-roles';

/**
 * Available sort metrics for Roles.
 */
export const RolesMetric = {
  ROLE: 'role',
  TENANT: 'tenant.id',
};

const AdminRoles = () => {
  const { t } = useTranslation(['admin.roles', 'general'], { useSuspense: false });

  const {
    tenantState: { selectedTenant },
  } = useContext(TenantContext);

  const [currentSearch, setCurrentSearch] = useState({});
  const [filterDefs, setFilterDefs] = useState({});

  const { showRoleDetails } = useContext(EntityDetailsContext);

  const { sortDirection, sortMetric, updateSortMetric } = useColumnState({
    columnDefs: RolesColumnDefs,
    defaultCols: tenantRolesCols,
    sortStateId: SORT_STATE_ID,
  });

  // eslint-disable-next-line no-unused-vars
  const handleRoleSelect = useCallback(
    (_, role) => {
      showRoleDetails(role.id);
    },
    [showRoleDetails],
  );

  const { data: { data: roles = [], filterValues } = {}, isRolesLoading } = useRolesTable({
    filters: filterDefs,
    search: currentSearch,
    tenantId: selectedTenant.id,
    sortMetric,
    sortDirection,
  });

  const handleFilter = useCallback(
    (updateDefs) => {
      setFilterDefs(updateDefs);
    },
    [setFilterDefs],
  );

  const handleFilterChange = useCallback((search) => setCurrentSearch(search), [setCurrentSearch]);

  return (
    <>
      <BackToGlobalTablesButton />
      <TenantAdminActionsHeader />
      <h1 className="pageTitle">{t('roles', 'Roles')}</h1>
      {isRolesLoading ? (
        <Loader />
      ) : (
        <RolesTable
          columns={tenantRolesCols}
          roles={roles}
          sortAction={updateSortMetric}
          sortMetric={sortMetric}
          sortDirection={sortDirection}
          onFilter={handleFilter}
          onFilterChange={handleFilterChange}
          filterValues={filterValues}
        />
      )}
    </>
  );
};

export default AdminRoles;
